import * as React from 'react';
import { useState, useEffect } from 'react';
import { SvgImage } from '../common';
import FlatFilterListProps, { FlatFilterListOption } from './type';
import breakpoints from '../../breakpoints';
import './styles.scss';

const FlatFilterList = ({
  name,
  queryParam,
  items,
  selectedValue,
  viewMoreLabel,
  action,
  allowMultipleSelection = false,
}: FlatFilterListProps) => {
  const defaultCutOffIndex = 5;
  const [cutOffIndex, setCutOffIndex] = useState<number>(defaultCutOffIndex);
  const [isViewMore, setIsViewmore] = useState<boolean>(
    (items.length - 1) >= cutOffIndex,
  );
  const [options, setOptions] = useState<FlatFilterListOption[]>();
  const [values, setValues] = useState<FlatFilterListOption[]>();

  /**
   * This remaps the options base of the items and selectedValue
   * props.
   * @returns
   */
  const mapSelection = () => {
    const mappedOptions: FlatFilterListOption[] = [];

    items.forEach((item) => {
      // Check for selected state for the filter with multiple selection
      const isArray = Array.isArray(selectedValue);
      const newValue = isArray ? selectedValue : selectedValue?.split(',');
      const isSelected = newValue?.findIndex((filter) => filter === item.value);

      mappedOptions.push(
        Object.assign(item, {
          name: queryParam,
          selectedValue: isSelected! >= 0,
          visible: true,
        }),
      );
    });

    return mappedOptions;
  };

  /**
   * Handles options change events
   * Triggers callback prop
   * @param event
   */
  const handleChange = (event: React.MouseEvent, optionIndex: number) => {
    event.preventDefault();
    setOptions(mapSelection());

    const getNewState = () => {
      if (!options) return undefined;

      const newState = values || [];

      const selectedOption = options[optionIndex];

      const isAllSelected = selectedOption.displayText.toLowerCase().startsWith('all');

      const existingIndex = newState.findIndex((opt) => (
        opt.displayText === selectedOption.displayText
      ));

      if (isAllSelected || !allowMultipleSelection || (!existingIndex && newState.length === 1)) {
        return [selectedOption];
      }

      if (existingIndex >= 0 && newState.length > 1) {
        return newState.filter((_, index) => index !== existingIndex);
      }

      const removedSelectionAll = newState.filter((s) => !s.displayText.toLowerCase().startsWith('all'));

      return [...removedSelectionAll, options[optionIndex]];
    };

    setValues(getNewState());

    if (action && typeof action === 'function') {
      action(getNewState());
    }
  };

  /**
   * View more action
   * @param event
   */
  const viewMoreAction = (event: React.MouseEvent) => {
    event.preventDefault();
    if (items.length > cutOffIndex) {
      setCutOffIndex(cutOffIndex + defaultCutOffIndex);
    }
  };

  /**
   * Handles the cutOffIndex value
   */
  const handleCutOffIndex = () => {
    const screenWidth = window.outerWidth;
    setCutOffIndex(
      screenWidth <= breakpoints.md ? defaultCutOffIndex : items.length,
    );
  };

  /**
   * Triggers the handleCutOffIndex
   */
  useEffect(() => {
    if (items.length) {
      handleCutOffIndex();
      window.addEventListener('resize', () => handleCutOffIndex());
    }

    return () => {
      window.removeEventListener('resize', () => handleCutOffIndex);
    };
  }, []);

  useEffect(() => {
    const optionsSelection = mapSelection();
    setOptions(optionsSelection);
  }, [selectedValue]);

  /**
   * Handles the isViewMore option.
   */
  useEffect(() => {
    setIsViewmore((items.length - 1) >= cutOffIndex);
  }, [cutOffIndex]);

  return (
    <div className="flat-filter-list">
      <div className="flat-filter-list__name">
        <h3>{name}</h3>
      </div>
      <div className="flat-filter-list__options">
        {options && (
          <ul>
            {options.slice(0, cutOffIndex).map((option, index) => (
              <li key={option.value}>
                <button
                  className={`
                    flat-filter-list__option
                    ${option.selectedValue && 'flat-filter-list__option--selected'}
                  `}
                  onClick={(event: React.MouseEvent) => handleChange(event, index)}
                >
                  {option.displayText}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {isViewMore && (
        <div className="flat-filter-list__view-more">
          <a
            href="#"
            className="link link--has-icon false false"
            onClick={(event: React.MouseEvent) => viewMoreAction(event)}
          >
            <span>{viewMoreLabel}</span>
            <div className="link__icon">
              <SvgImage title="arrow" reference="#icon-plus" />
            </div>
          </a>
        </div>
      )}
    </div>
  );
};

export default FlatFilterList;
